import { ChangeEvent, FC, useCallback, useEffect, useRef, useState } from "react"
import { toast } from "react-toastify"

import { useTranslation } from "react-i18next"

import { ModalConfirmAction } from "components/modals/ModalConfirmAction"
import { Column, DataTable } from "components/tables/DataTable"

import { cmsSeoService } from "services/cms-seo/cms-seo.service"
import { CmsRedirect, CmsRedirectOrigin } from "services/cms-seo/type"

import { AsyncList, CodeBadge, dangerButtonClass, FilterField, ghostButtonClass, InfoBox, inputClass, PathText, primaryButtonClass } from "../CmsUi"
import { formatDateTime } from "../helpers/format"
import { RedirectFormState, RedirectModal } from "../RedirectModal"

const ORIGIN_LABELS: Record<CmsRedirectOrigin, string> = {
  manual: "Manual",
  slug_change: "Slug change",
  bulk_import: "Bulk import",
  expired_event: "Expired event"
}

export const RedirectsTab: FC = () => {
  const { t } = useTranslation("translation", { useSuspense: false })

  const [rows, setRows] = useState<CmsRedirect[]>([])
  const [loading, setLoading] = useState(false)
  const [search, setSearch] = useState("")
  const [typeFilter, setTypeFilter] = useState<"" | "301" | "302" | "auto">("")
  const [formState, setFormState] = useState<RedirectFormState>({ kind: "none" })
  const [deleteCandidate, setDeleteCandidate] = useState<CmsRedirect | null>(null)
  const fileInput = useRef<HTMLInputElement>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setRows(await cmsSeoService.listRedirects())
    setLoading(false)
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const onImport = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    event.target.value = ""
    if (!file) return

    const result = await cmsSeoService.importRedirects(file)
    if (!result) return

    toast.success(
      t("cmsSeo.importDone", {
        defaultValue: "Imported {{imported}} redirects, skipped {{skipped}}",
        imported: result.imported,
        skipped: result.skipped
      }) as string
    )
    result.errors.slice(0, 5).forEach((message) => toast.warning(message))
    load()
  }

  const confirmDelete = async (row: CmsRedirect) => {
    if (await cmsSeoService.deleteRedirect(row.id)) {
      toast.success(t("cmsSeo.redirectDeleted", { defaultValue: "Redirect deleted" }) as string)
      load()
    }
  }

  const needle = search.trim().toLowerCase()
  const visible = rows.filter((row) => {
    if (typeFilter === "301" && row.code !== 301) return false
    if (typeFilter === "302" && row.code !== 302) return false
    if (typeFilter === "auto" && row.origin === "manual") return false
    if (!needle) return true
    return row.source.toLowerCase().includes(needle) || row.target.toLowerCase().includes(needle)
  })

  const columns: Column<CmsRedirect>[] = [
    { header: t("cmsSeo.redirect.source", { defaultValue: "Source" }), render: (row) => <PathText>{row.source}</PathText> },
    { header: t("cmsSeo.redirect.target", { defaultValue: "Target" }), render: (row) => <PathText>{row.target}</PathText> },
    {
      header: t("cmsSeo.redirect.code", { defaultValue: "Code" }),
      hideBelow: "sm",
      render: (row) => <CodeBadge code={row.code} />
    },
    {
      header: t("cmsSeo.redirect.origin", { defaultValue: "Origin" }),
      hideBelow: "lg",
      render: (row) => <span className="text-xs text-gray-500 dark:text-gray-400">{ORIGIN_LABELS[row.origin]}</span>
    },
    {
      header: t("cmsSeo.redirect.hits", { defaultValue: "Hits (30d)" }),
      hideBelow: "md",
      render: (row) => <span className="text-sm">{row.hits_30d.toLocaleString()}</span>
    },
    {
      header: t("cmsSeo.redirect.lastHit", { defaultValue: "Last hit" }),
      hideBelow: "xl",
      render: (row) => <span className="text-xs text-gray-500 dark:text-gray-400">{formatDateTime(row.last_hit_at)}</span>
    },
    {
      header: t("common.actions", { defaultValue: "Actions" }),
      align: "right",
      render: (row) => (
        <div className="flex justify-end gap-2">
          <button
          type="button"
          className={primaryButtonClass}
          onClick={() => setFormState({ kind: "edit", record: row })}>
            {t("common.edit", { defaultValue: "Edit" })}
          </button>
          <button
            type="button"
            className={dangerButtonClass}
            onClick={() => setDeleteCandidate(row)}
          >
            {t("common.delete", { defaultValue: "Delete" })}
          </button>
        </div>
      )
    }
  ]

  return (
    <div className="flex flex-col gap-5 p-4 sm:p-5">
      <div className="flex flex-wrap items-end gap-4">
        <FilterField label={t("cmsSeo.filter.type", { defaultValue: "Type" })}>
          <select className={inputClass} value={typeFilter} onChange={(event) => setTypeFilter(event.target.value as typeof typeFilter)}>
            <option value="">{t("common.all", { defaultValue: "All" })}</option>
            <option value="301">301 — permanent</option>
            <option value="302">302 — temporary</option>
            <option value="auto">{t("cmsSeo.redirect.autoCreated", { defaultValue: "Auto-created" })}</option>
          </select>
        </FilterField>

        <FilterField label={t("common.search", { defaultValue: "Search" })} grow>
          <input
            className={inputClass}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={t("cmsSeo.filter.searchRedirects", { defaultValue: "Search source or target path…" }) as string}
          />
        </FilterField>

        <input ref={fileInput} type="file" accept=".csv,text/csv" className="hidden" onChange={onImport} />
        <button type="button" className={ghostButtonClass} onClick={() => fileInput.current?.click()}>
          {t("cmsSeo.importCsv", { defaultValue: "Import CSV" })}
        </button>
        <button
          type="button"
          className={`${primaryButtonClass} w-full sm:w-auto`}
          onClick={() => setFormState({ kind: "create" })}>
          {t("cmsSeo.addRedirectShort", { defaultValue: "+ Add redirect" })}
        </button>
      </div>

      <InfoBox>
        {t("cmsSeo.redirectsInfo", {
          defaultValue:
            "Redirects run at the edge before the page is rendered. An exact source beats a wildcard, and chains are collapsed so a visitor never follows more than one hop. CSV columns: source,target,code."
        })}
        <div className="mt-2">
          {t("cmsSeo.redirectsPropagation", {
            defaultValue:
              "The edge holds the redirect list for up to 60 seconds, so a new rule takes up to a minute to start working. Paths are locale-less: /lol covers /en/lol and every other language. Redirects away from a language code the site no longer uses are handled for you and need no rule here."
          })}
        </div>
      </InfoBox>

      <h2 className="text-base font-semibold text-black dark:text-white">{t("cmsSeo.tab.redirects", { defaultValue: "Redirects" })}</h2>

      <AsyncList
        loading={loading}
        isEmpty={visible.length === 0}
        emptyText={t("cmsSeo.noRedirects", { defaultValue: "No redirects configured." }) as string}
      >
        <DataTable rows={visible} rowKey={(row) => row.id} columns={columns} />
      </AsyncList>

      <RedirectModal
        state={formState}
        onClose={() => setFormState({ kind: "none" })}
        onSaved={() => {
          setFormState({ kind: "none" })
          load()
        }}
      />

      <ModalConfirmAction
        data={deleteCandidate}
        handler={() => setDeleteCandidate(null)}
        text={
          deleteCandidate
            ? (t("cmsSeo.confirmDeleteRedirect", {
                defaultValue: "Delete the redirect from {{source}}? Visitors and crawlers will get a 404 instead.",
                source: deleteCandidate.source
              }) as string)
            : ""
        }
        actionText={t("common.delete", { defaultValue: "Delete" })}
        actionClassName="btn-error"
        actionHandler={confirmDelete}
      />
    </div>
  )
}
